"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment, useClient } from "@opensea/graphql";
import { usePathname } from "@opensea/navigation";
import { TabMenu, TabMenuItem, TabMenuItemTitle } from "@opensea/next-ui-kit";
import { Tooltip } from "@opensea/ui-kit";
import { notFound, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { prefetchDropEligibilityQuery } from "@/components/collection/drops/drop-store";
import { prefetchCollectionBestDealsQuery } from "@/components/collection/explore/components/CollectionBestDeals/query";
import { prefetchCollectionRarestItemsQuery } from "@/components/collection/explore/components/CollectionRarestItems/query";
import { prefetchCollectionRecentSalesQuery } from "@/components/collection/explore/components/CollectionRecentSales/query";
import { prefetchCollectionTopSalesQuery } from "@/components/collection/explore/components/CollectionTopSales/query";
import { prefetchCollectionHoldersQuery, prefetchCollectionHoldersQueryPage, prefetchCollectionTopOfferQuery } from "@/components/items/collection/CollectionHolders/CollectionHoldersTable/query";
import { prefetchCollectionItemsActionBarQuery } from "@/components/items/collection/CollectionItemsActionBar/query";
import { prefetchCollectionItemsCountQuery } from "@/components/items/collection/CollectionItemsCountProvider/query";
import { prefetchCollectionItemsListQuery } from "@/components/items/collection/CollectionItemsList/query";
import { prefetchCollectionTraitItemsListQuery } from "@/components/items/collection/CollectionTraitItems/CollectionTraitItemsList/query";
import { useNavigationContainerClassNames, useNavigationTabMenuClassNames } from "@/components/navigation/constants";
import { prefetchCollectionOffersActionBarQuery } from "@/components/offers/collection/CollectionOffersActionBar/query";
import { prefetchCollectionOffersTableQuery } from "@/components/offers/collection/CollectionOffersTable/query";
import { prefetchUseCollectionActivityQuery } from "@/hooks/api/activity/use-collection-activity";
import { prefetchUseCollectionOfferTotalsQuery } from "@/hooks/api/orders/use-collection-offer-totals";
import { prefetchUseTraitOfferAggregatesTotal } from "@/hooks/api/orders/use-trait-offer-aggregates-total";
import { prefetchCollectionAttributes } from "@/hooks/api/use-collection-attributes";
import { QUERY_PARAM_KEYS } from "@/hooks/query-param/keys";
import { useAddress } from "@/hooks/wallet";
import { CollectionNavigationFragment } from "./CollectioNavigation.fragment";
import { useCollectionTabs, useIsItemActive } from "./use-collection-tabs";
export function CollectionNavigation(t0) {
  const $ = _c(14);
  const {
    collection: collectionKey
  } = t0;
  let t1;
  if ($[0] !== collectionKey) {
    t1 = readFragment(CollectionNavigationFragment, collectionKey);
    $[0] = collectionKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const collection = t1;
  const isItemActive = useIsItemActive(collection.slug);
  const collectionTabs = useCollectionTabs(collection);
  const navigationTabMenuClassNames = useNavigationTabMenuClassNames();
  const navigationContainerClassNames = useNavigationContainerClassNames();
  const pathname = usePathname();
  if (pathname.includes("collection") && !pathname.includes("preview") && !collectionTabs.some(tab => isItemActive(tab))) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = notFound();
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== collection.slug || $[4] !== collectionTabs) {
    let t3;
    if ($[6] !== collection.slug) {
      t3 = tab_0 => <CollectionNavigationItem collectionSlug={collection.slug} key={tab_0.href} tab={tab_0} />;
      $[6] = collection.slug;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = collectionTabs.map(t3);
    $[3] = collection.slug;
    $[4] = collectionTabs;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[8] !== navigationTabMenuClassNames || $[9] !== t2) {
    t3 = <TabMenu aria-label="Collection" className={navigationTabMenuClassNames}>{t2}</TabMenu>;
    $[8] = navigationTabMenuClassNames;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== navigationContainerClassNames || $[12] !== t3) {
    t4 = <div className={navigationContainerClassNames}>{t3}</div>;
    $[11] = navigationContainerClassNames;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function CollectionNavigationItem(t0) {
  const $ = _c(25);
  const {
    tab,
    collectionSlug
  } = t0;
  const isActive = useIsItemActive(collectionSlug);
  let t1;
  if ($[0] !== isActive || $[1] !== tab) {
    t1 = isActive({
      ...tab
    });
    $[0] = isActive;
    $[1] = tab;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const active = t1;
  const t = useTranslations("CollectionNavigation");
  const searchParams = useSearchParams();
  const client = useClient();
  const address = useAddress();
  let href = tab.href;
  const traits = searchParams.get(QUERY_PARAM_KEYS.traits);
  if (traits) {
    href = `${tab.href}?${QUERY_PARAM_KEYS.traits}=${encodeURIComponent(traits)}`;
  }
  let t2;
  if ($[3] !== active || $[4] !== address || $[5] !== client || $[6] !== collectionSlug || $[7] !== tab) {
    t2 = () => {
      if (active || tab.disabled) {
        return;
      }
      prefetchCollectionTab(tab, collectionSlug, client, address);
    };
    $[3] = active;
    $[4] = address;
    $[5] = client;
    $[6] = collectionSlug;
    $[7] = tab;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const prefetch = t2;
  let t3;
  if ($[9] !== t) {
    t3 = t("comingSoon");
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = !tab.disabled;
  const t5 = active ? "page" : undefined;
  let t6;
  if ($[11] !== tab.title) {
    t6 = <TabMenuItemTitle>{tab.title}</TabMenuItemTitle>;
    $[11] = tab.title;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== active || $[14] !== href || $[15] !== prefetch || $[16] !== t5 || $[17] !== t6 || $[18] !== tab.disabled) {
    t7 = <div><TabMenuItem active={active} aria-current={t5} disabled={tab.disabled} href={href} onPointerOver={prefetch}>{t6}</TabMenuItem></div>;
    $[13] = active;
    $[14] = href;
    $[15] = prefetch;
    $[16] = t5;
    $[17] = t6;
    $[18] = tab.disabled;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t7 || $[23] !== tab.title) {
    t8 = <Tooltip content={t3} disabled={t4} key={tab.title}>{t7}</Tooltip>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t7;
    $[23] = tab.title;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function prefetchCollectionTab(tab, collectionSlug, client, address) {
  if (tab.id === "explore") {
    prefetchCollectionRecentSalesQuery(collectionSlug, client);
    prefetchCollectionTopSalesQuery(collectionSlug, client);
    prefetchCollectionRarestItemsQuery(collectionSlug, client);
    prefetchCollectionBestDealsQuery(collectionSlug, client);
  } else if (tab.id === "items") {
    prefetchCollectionItemsListQuery(collectionSlug, client);
    prefetchCollectionItemsActionBarQuery(collectionSlug, client);
    prefetchCollectionItemsCountQuery(collectionSlug, client);
  } else if (tab.id === "tokens") {
    // TODO(figure out a way to prefetch based on token address)
  } else if (tab.id === "offers") {
    prefetchUseTraitOfferAggregatesTotal(collectionSlug, client);
    prefetchUseCollectionOfferTotalsQuery(collectionSlug, client);
    prefetchCollectionOffersTableQuery(collectionSlug, client);
    prefetchCollectionOffersActionBarQuery(collectionSlug, client);
  } else if (tab.id === "holders") {
    prefetchCollectionHoldersQueryPage(collectionSlug, client);
    prefetchCollectionTopOfferQuery(collectionSlug, client);
    prefetchCollectionHoldersQuery(collectionSlug, client);
  } else if (tab.id === "traits") {
    prefetchCollectionAttributes(collectionSlug, client);
    prefetchCollectionTraitItemsListQuery(collectionSlug, client);
  } else if (tab.id === "activity") {
    prefetchUseCollectionActivityQuery(collectionSlug, client);
  } else if (tab.id === "overview") {
    if (address?.length) {
      prefetchDropEligibilityQuery(collectionSlug, client, address);
    }
    // TODO(figure out a way to prefetch based on multiple slugs and preview item id's)
  }
}